import React, { useState } from 'react';
import { getCreatorPayments, usePaginatedFetch } from '@round/api';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import useOnScroll from '../../../../hooks/useOnScroll';
import styles from '../CreatorPayments.module.css';
import { Select, Skeleton } from '@round/ui-kit';
import PastPaymentsTable from './PastPaymentsTable/PastPaymentsTable';
import useUrlState from '../../../../hooks/useUrlState';
import { DebounceInput } from 'react-debounce-input';
import useFinanceManagerSelect from '../useFinanceManagerSelect';
import { filterSelectStyles } from '../helpers';
import useCreatorSelect from '../../../influencer/useCreatorSelect';

type UrlState = {
    booking_id: number;
    manager_id: number;
    influencer_id: number;
};

const PastPayments = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>({}, { manager_id: 'number', influencer_id: 'number' });
    const [page, setPage] = useState(1);

    const managerSelectProps = useFinanceManagerSelect(urlState.manager_id);
    const influencerSelectProps = useCreatorSelect(urlState.influencer_id);

    const {
        data: pastCreatorPayments,
        getCurrentPage: fetchPastCreatorPayments,
        loading: pastCreatorPaymentsLoading,
        hasNextPage: pastCreatorPaymentsHasNextPage,
        count: pastCreatorPaymentsCount,
        reset: resetPastCreatorPayments,
    } = usePaginatedFetch(getCreatorPayments, {
        page,
        page_size: 30,
        booking_id: urlState.booking_id,
        manager_id: urlState.manager_id,
        influencer_id: urlState.influencer_id,
    });

    useAbortableEffect(
        (signal) => {
            fetchPastCreatorPayments({ signal });
        },
        [fetchPastCreatorPayments]
    );

    const pastPaymentsRowCountLabel = pastCreatorPaymentsCount
        ? `Showing ${pastCreatorPayments.length} of ${pastCreatorPaymentsCount}`
        : null;

    const handlePastPaymentsBottomReached = useOnScroll(() => {
        if (!pastCreatorPaymentsLoading && pastCreatorPaymentsHasNextPage) {
            setPage((page) => page + 1);
        }
    });

    return (
        <>
            <div className={styles.panelHeader}>
                <div className={styles.filters}>
                    <div className={styles.filter}>
                        <span className={styles.label}>Booking ID</span>
                        <DebounceInput
                            type="number"
                            className={styles.filterInput}
                            placeholder="Enter booking id..."
                            value={urlState.booking_id}
                            onChange={(e) => {
                                setUrlState({ booking_id: e.target.value ? parseInt(e.target.value) : undefined });
                                setPage(1);
                                resetPastCreatorPayments();
                            }}
                            debounceTimeout={700}
                        />
                    </div>

                    <div className={styles.filter}>
                        <span className={styles.label}>Manager</span>
                        <Select
                            isClearable
                            styles={filterSelectStyles}
                            placeholder="Search manager..."
                            {...managerSelectProps}
                            onChange={(value, action) => {
                                if (typeof managerSelectProps.onChange === 'function') {
                                    managerSelectProps.onChange(value, action);
                                }
                                setUrlState({ manager_id: value?.value });
                                resetPastCreatorPayments();
                                setPage(1);
                            }}
                        />
                    </div>

                    <div className={styles.filter}>
                        <span className={styles.label}>Creator</span>
                        <Select
                            isClearable
                            styles={filterSelectStyles}
                            placeholder="Search creator..."
                            {...influencerSelectProps}
                            onChange={(value, action) => {
                                if (typeof influencerSelectProps.onChange === 'function') {
                                    influencerSelectProps.onChange(value, action);
                                }
                                setUrlState({ influencer_id: value?.value });
                                resetPastCreatorPayments();
                                setPage(1);
                            }}
                        />
                    </div>
                </div>

                <span className={styles.label}>
                    {pastCreatorPaymentsLoading ? <Skeleton /> : pastPaymentsRowCountLabel}
                </span>
            </div>
            <div className={styles.tableScrollContainer} onScroll={handlePastPaymentsBottomReached}>
                <PastPaymentsTable data={pastCreatorPayments} loading={pastCreatorPaymentsLoading} />
            </div>
        </>
    );
};

export default PastPayments;
